/**
 * Tool-call provenance labels — what a brand user is told was checked (VRG-227).
 *
 * ── THE DRIFT THIS FIXES ─────────────────────────────────────────────────────────────
 * This used to live inline in `AskMessageList.tsx`, keyed on the TOOL NAME:
 *
 *   const TOOL_LABELS: Record<string, string> = {
 *     query_scans: 'Checked your scans',
 *     query_certificates: 'Checked your certificates',
 *     query_owners: 'Checked your owners',
 *   }
 *
 * Earlier the same day, the server collapsed its three fixed tools
 * (`query_scans`/`query_certificates`/`query_owners`) into ONE tool, `query`, that takes a
 * `metric` field instead (ask.tools.ts). Every real tool call is now named `query`, so the
 * map above stopped matching ANYTHING — every provenance chip silently fell through to the
 * generic `?? 'Checked your data'` fallback. No type error, no failing test: the fallback
 * is exactly what made a total loss of specificity look like a working feature.
 *
 * The fix is keyed on the METRIC (`call.input.metric`) instead of the tool name, since the
 * tool name alone is now uninformative — and `METRIC_LABELS` is `Record<AskMetricName,
 * string>`, a TOTAL map over the metric union, not a partial one with a `??` escape hatch.
 * Adding a sixth metric to `AskMetricName` (toolResult.ts) without adding it here is now a
 * COMPILE ERROR in this file — the strongest version of "can't forget," per
 * working-conventions' guidance to prefer a type-level fix over a runtime fallback where
 * the union is available. The one residual fallback (`UNKNOWN_METRIC_LABEL`) only fires
 * for genuinely malformed input (a missing/non-string `metric`, which should never happen
 * given `parseToolInput`'s validation, but this file has no way to prove that from here) —
 * it is defence in depth, not the primary mechanism.
 *
 * `ask-tool-coverage.guard.spec.ts` (apps/api/tests/unit/) is the OTHER half: it derives
 * the server's live metric set (`ASK_METRIC_NAMES`) and asserts every one of them has an
 * entry here, so a metric added to the server without a client label fails a guard rather
 * than silently falling back at runtime. It covers the `query` tool's metrics only —
 * `market_stats` (below) is a second, INDEPENDENT tool with no metric enum of its own to
 * cover the same way; see MARKET_STATS_LABEL's own comment.
 */
import type { AskToolCall } from './types'
import type { AskMetricName } from './toolResult'

const METRIC_LABELS: Record<AskMetricName, string> = {
  scans: 'Checked your scans',
  certificates: 'Checked your certificates',
  owners: 'Checked your owners',
  leads: 'Checked your leads',
  transfers: 'Checked your transfers',
  /**
   * VRG-567 — "Checked your BILLING", not "your data", and the wording is the point.
   *
   * These three are the only metrics that read a money figure the caller pays, so the
   * provenance chip is the one place a reader is told the assistant looked at billing
   * rather than at scan counts. `return_on_vera` reads both sides of the division, so it
   * names both rather than claiming to have checked only one.
   */
  return_on_vera: 'Checked your Vera spend and value delivered',
  value_delivered: 'Checked the value Vera delivered',
  vera_spend: 'Checked your Vera billing',
}

/** Exported for `ask-tool-coverage.guard.spec.ts` to derive coverage from — not for UI use. */
export { METRIC_LABELS as ASK_METRIC_LABELS }

const isLabeledMetric = (value: unknown): value is AskMetricName =>
  typeof value === 'string' && Object.prototype.hasOwnProperty.call(METRIC_LABELS, value)

const UNKNOWN_METRIC_LABEL = 'Checked your data'

/**
 * VRG-464 — `market_stats` is a SEPARATE tool name (not a `query` metric), so it is keyed
 * on `call.name` directly rather than `call.input.metric` the way the five metrics above
 * are — this is the one real tool call where the tool NAME is informative again, because
 * unlike `query` (always named `query`, see the module docblock), `market_stats` is the
 * only tool ever named `market_stats`. A single fixed label, not a `Record`, because there
 * is only one — no per-caller variant the way the five `query` metrics have.
 */
const MARKET_STATS_LABEL = 'Checked market data'

/**
 * The label shown on a tool-call chip. `market_stats` is keyed on `call.name`; every
 * other real call is named `query`, so `call.name` alone says nothing for those and this
 * falls through to `call.input.metric` (see the module docblock).
 */
export const toolCallLabel = (call: AskToolCall): string => {
  if (call.name === 'market_stats') return MARKET_STATS_LABEL
  const metric = call.input.metric
  return isLabeledMetric(metric) ? METRIC_LABELS[metric] : UNKNOWN_METRIC_LABEL
}
